import { defaultTimerAlerts } from "@/components/settings/timer-configuration/types"
import { FIVE_MIN_SECONDS, ONE_MIN_SECONDS, PHASE_LABELS } from "@/constants/Timeline"
import { PhaseStatus, PhaseType, TimerAlertsConfig } from "@/redux/slices/timerConfigurationSlice"
import store from "@/redux/store"
import { toast } from "sonner"

let timerAlertState: {
    contextKey: string
    prevSeconds: number | null
  } | null = null
  
export function getCurrentRoomIdFromStore(): string | null {
    return store.getState().room.currentRoom?.roomid ?? null
  }
  
export let lastKnownRoundNumber: number | null = null
  
export function mergeTimerAlertsFromMessage(data: {
    alerts?: Partial<TimerAlertsConfig>
  }): TimerAlertsConfig {
    const fromStore = store.getState().timerConfiguration.config?.alerts
    return {
      ...defaultTimerAlerts,
      ...fromStore,
      ...(data.alerts && typeof data.alerts === 'object' ? data.alerts : {}),
    }
  }
  
  export function secondsRemainingUntil(iso: string | null): number | null {
    if (!iso) return null
    const sec = Math.floor((new Date(iso).getTime() - Date.now()) / 1000)
    if (sec < 0) return 0
    return sec
  }
  
  export function getManualModeMinSecondsRemaining(
    phases: PhaseStatus[]
  ): number | null {
    let min: number | null = null
    for (const p of phases) {
      if (!p.isActive || p.isInfinite || !p.endTime) continue
      const s = secondsRemainingUntil(p.endTime)
      if (s === null) continue
      if (min === null || s < min) min = s
    }
    return min
  }
  
 export function manualModeAlertContextKey(phases: PhaseStatus[]): string {
    const ids = phases
      .filter((p) => p.isActive && p.endTime && !p.isInfinite)
      .map((p) => p.id)
      .sort((a, b) => a - b)
    return ids.length ? `manual-${ids.join('-')}` : 'manual-none'
  }
  
  export function manualModePhaseLabelForAlert(phases: PhaseStatus[]): string {
    let best: PhaseStatus | null = null
    let bestSec = Infinity
    const now = Date.now()
    for (const p of phases) {
      if (!p.isActive || p.isInfinite || !p.endTime) continue
      const sec = Math.floor((new Date(p.endTime).getTime() - now) / 1000)
      if (sec >= 0 && sec < bestSec) {
        bestSec = sec
        best = p
      }
    }
    if (best) return getPhaseName(best.phaseType)
    return 'this phase'
  }
  
  export function maybeToastTimeThresholdAlerts(params: {
    alerts: TimerAlertsConfig
    contextKey: string
    currentSeconds: number | null
    phaseLabel: string
    currentRound: number
  }) {
    const { alerts, contextKey, currentSeconds, phaseLabel, currentRound } =
      params
  
    if (currentSeconds === null) return
  
    if (!timerAlertState || timerAlertState.contextKey !== contextKey) {
      timerAlertState = { contextKey, prevSeconds: null }
    }
  
    const prev = timerAlertState.prevSeconds
    timerAlertState.prevSeconds = currentSeconds
  
    if (prev === null) return
  
    if (
      alerts.enableFiveMinuteVisual &&
      prev > FIVE_MIN_SECONDS &&
      currentSeconds <= FIVE_MIN_SECONDS &&
      currentSeconds > 0
    ) {
      toast.warning(
        `About 5 minutes left in ${phaseLabel} (Round ${currentRound})`,
        { duration: 6000 }
      )
    }
    if (
      alerts.enableOneMinuteVisual &&
      prev > ONE_MIN_SECONDS &&
      currentSeconds <= ONE_MIN_SECONDS &&
      currentSeconds > 0
    ) {
      toast.warning(
        `About 1 minute left in ${phaseLabel} (Round ${currentRound})`,
        { duration: 8000 }
      )
    }
  }
  
  export function getPhaseName(phaseType: PhaseType | undefined): string {
    if (!phaseType) return 'waiting for game to start'

    return PHASE_LABELS[phaseType] ?? 'waiting for game to start'
  }